import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import config from "../../config";
import { formatCurrency } from "../../utils/formatCurrency";
import "./overlay.css";

export default function TipAlertOverlay() {
  const { username } = useParams();
  const [searchParams] = useSearchParams();
  const [currentAlert, setCurrentAlert] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const lastSeenIdRef = useRef(null);
  const alertQueueRef = useRef([]);
  const isShowingRef = useRef(false);
  const audioRef = useRef(null);

  // Fetch user profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${config.API_URL}/users/username/${username}/profile`);
        if (res.ok) {
          const profile = await res.json();
          setUserProfile(profile);

          // Initialize lastSeenId with the most recent donation
          const donationsRes = await fetch(`${config.API_URL}/donations/recent?creator_id=${profile.id}&limit=1&completed_only=true`);
          if (donationsRes.ok) {
            const donations = await donationsRes.json();
            if (donations.length > 0) {
              lastSeenIdRef.current = donations[0].id;
            }
          }
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };

    fetchProfile();
  }, [username]);

  // Show next alert from queue
  const showNextAlert = () => {
    if (alertQueueRef.current.length === 0) {
      isShowingRef.current = false;
      return;
    }

    isShowingRef.current = true;
    const nextAlert = alertQueueRef.current.shift();
    setCurrentAlert(nextAlert);
    setIsVisible(true);

    // Play notification sound
    const audio = new Audio("/castle_tip_notification.mp3");
    audio.play().catch(() => {});

    // Hide after 8 seconds, then show next
    setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentAlert(null);
        showNextAlert();
      }, 500); // Wait for fade out animation
    }, 8000);
  };

  // Queue a new alert
  const queueAlert = (donation) => {
    alertQueueRef.current.push(donation);
    if (!isShowingRef.current) {
      showNextAlert();
    }
  };

  const isTestMode = searchParams.get('cstldebug') === '1';

  const testNames = ['BigFan99', 'StreamLover', 'Anonymous', 'NightOwl', 'GamerPro', 'CoolDude42'];
  const testMessages = [
    'This is a test tip message! Looking great on stream!',
    'Keep up the great content!',
    'Love watching your streams!',
    '',
    'You deserve this!',
    'Amazing stream today! I just wanted to say that I have been watching your streams for years and you are by far my favorite streamer. Keep doing what you do because it really makes my day every single time I tune in!',
  ];
  const testAmounts = [5, 10, 25, 50, 1, 100];

  const fireTestAlert = () => {
    const testDonation = {
      id: 'test-' + Date.now(),
      name: testNames[Math.floor(Math.random() * testNames.length)],
      amount: testAmounts[Math.floor(Math.random() * testAmounts.length)],
      message: testMessages[Math.floor(Math.random() * testMessages.length)]
    };
    queueAlert(testDonation);
  };

  // Poll for new donations
  useEffect(() => {
    if (!userProfile?.id) return;

    let intervalId = null;

    const checkForNewDonations = async () => {
      try {
        const res = await fetch(`${config.API_URL}/donations/recent?creator_id=${userProfile.id}&limit=5&completed_only=true`);
        if (res.ok) {
          const donations = await res.json();

          if (donations.length > 0) {
            // Find new donations (those with ID greater than lastSeenId)
            const newDonations = [];
            for (const donation of donations) {
              if (lastSeenIdRef.current === null || donation.id > lastSeenIdRef.current) {
                newDonations.push(donation);
              }
            }

            // Queue alerts for new donations (oldest first)
            newDonations.reverse().forEach(donation => {
              queueAlert(donation);
            });

            // Update lastSeenId
            if (donations.length > 0) {
              lastSeenIdRef.current = donations[0].id;
            }
          }
        }
      } catch (error) {
        console.log("Error checking for donations:", error);
      }
    };

    // Set up polling
    const setupPolling = async () => {
      try {
        const res = await fetch(`${config.API_URL}/settings/donation-polling-interval`);
        if (res.ok) {
          const { polling_interval } = await res.json();
          if (polling_interval > 0) {
            intervalId = setInterval(checkForNewDonations, polling_interval * 1000);
          }
        }
      } catch (error) {
        console.log("Using default polling interval");
        intervalId = setInterval(checkForNewDonations, 5000);
      }
    };

    setupPolling();

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [userProfile?.id]);

  return (
    <div className="tip-alert-container">
      {currentAlert && (
        <div className={`tip-alert ${isVisible ? 'tip-alert-visible' : 'tip-alert-hidden'}`}>
          <div className="tip-alert-header">
            <span className="tip-alert-label">New Tip!</span>
          </div>
          <div className="tip-alert-content">
            <div className="tip-alert-amount">
              {formatCurrency(currentAlert.amount)}
            </div>
            <div className="tip-alert-from">
              from <span className="tip-alert-name">{currentAlert.name || "Anonymous"}</span>
            </div>
            {currentAlert.message && (
              <div className="tip-alert-message">
                "{currentAlert.message.length > 150 ? currentAlert.message.substring(0, 150) + '...' : currentAlert.message}"
              </div>
            )}
          </div>
        </div>
      )}
      {isTestMode && (
        <button
          onClick={fireTestAlert}
          style={{
            position: 'fixed',
            bottom: '1rem',
            right: '1rem',
            padding: '0.75rem 1.5rem',
            background: '#99C2FF',
            color: '#0A0B0D',
            border: 'none',
            borderRadius: '0.5rem',
            fontWeight: 700,
            cursor: 'pointer',
            fontSize: '0.875rem',
            zIndex: 100,
          }}
        >
          Send Test Tip
        </button>
      )}
    </div>
  );
}
